import { Tabs, type TabsProps } from 'antd';
import { CanceledError } from 'axios';
import { Formik } from 'formik';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import './VendorLeadStepDescription.scss';

import { vendorsApi } from 'src/app/services/Vendors/Vendors.service';
import { VendorLeadStepFieldsTab } from 'src/components/vendors/steps/VendorLeadStepFieldsTab';
import { VendorLeadStepLayout } from 'src/components/vendors/steps/VendorLeadStepLayout';
import type { IVendorLeadDescriptionStepFormValues } from 'src/components/vendors/VendorLeadTypes';
import {
  formProductLeadSpecificationEditValidationSchema,
  toFormVendorProductLeadSpecificationEditInitialValues,
  toVendorLeadProductCreateOrUpdateSpecificationData,
} from 'src/components/vendors/VendorLeadValidation';
import { VendorsContext } from 'src/pages/VendorsPage/VendorsPage.context';
import {
  PRODUCT_SPECIFICATION_FIELD_GROUP_MAPPER,
  ProductSpecificationFieldGroup,
} from 'src/types/reference/ProductSpecificationFieldGroup';

const VendorLeadStepDescriptionSubSteps: Partial<Record<string, string>> = {
  [ProductSpecificationFieldGroup.Quantities]: ProductSpecificationFieldGroup.ProductDetails,
  [ProductSpecificationFieldGroup.ProductDetails]: ProductSpecificationFieldGroup.ShippingDetails,
};

type VendorLeadStepDescriptionProps = {
  vendorProductLeadId: string;
};

export function VendorLeadStepDescription({ vendorProductLeadId }: Readonly<VendorLeadStepDescriptionProps>) {
  const [currentTab, setCurrentTab] = useState<string>(ProductSpecificationFieldGroup.Quantities);

  const { vendorLeadProduct, vendorLeadProductSpecification, updateVendorLeadProduct, setVendorStepGuideKey } =
    useContext(VendorsContext);

  const [, partialUpdateVendorLeadProduct, cancelPartialUpdateVendorLeadProduct] =
    vendorsApi.usePartialUpdateVendorProductLeadMutation(vendorProductLeadId);

  const save = useCallback(
    async (newValues: IVendorLeadDescriptionStepFormValues, submit: boolean) => {
      if (!vendorLeadProductSpecification) {
        throw new Error('Product specification is not found');
      }

      cancelPartialUpdateVendorLeadProduct();

      try {
        const updatedVendorProductLeadData = await partialUpdateVendorLeadProduct({
          data: {
            initialQty: newValues.initialQty,

            fieldGroupData: {
              fieldGroup: currentTab,
              specData: toVendorLeadProductCreateOrUpdateSpecificationData(
                newValues.specData,
                vendorLeadProductSpecification,
                currentTab
              ),
              submit,
            },
          },
        });

        updateVendorLeadProduct(updatedVendorProductLeadData.data);
      } catch (unknownError) {
        if (unknownError instanceof CanceledError) {
          console.debug('Description: auto save request has been cancelled');
        } else {
          console.error(unknownError);
        }
      }
    },
    [
      vendorLeadProductSpecification,
      cancelPartialUpdateVendorLeadProduct,
      partialUpdateVendorLeadProduct,
      currentTab,
      updateVendorLeadProduct,
    ]
  );

  const handleSubmit = useCallback(
    async (submitValues: IVendorLeadDescriptionStepFormValues) => {
      await save(submitValues, true);

      const newSubStep = VendorLeadStepDescriptionSubSteps[currentTab];

      const moveToNextStep = newSubStep === undefined;

      if (!moveToNextStep) {
        setCurrentTab(newSubStep);
      }

      // Go to next step if empty
      return moveToNextStep;
    },
    [save, currentTab]
  );

  const handleValuesChange = useCallback(
    (saveValues: IVendorLeadDescriptionStepFormValues) => save(saveValues, false),
    [save]
  );

  const tabs: Exclude<TabsProps['items'], undefined> = useMemo(
    () =>
      (vendorLeadProductSpecification &&
        [
          ProductSpecificationFieldGroup.Quantities,
          ProductSpecificationFieldGroup.ProductDetails,
          ProductSpecificationFieldGroup.ShippingDetails,
        ].map((tabFieldGroup) => ({
          key: tabFieldGroup,
          label: PRODUCT_SPECIFICATION_FIELD_GROUP_MAPPER[tabFieldGroup],
          children: (
            <VendorLeadStepFieldsTab
              fieldGroup={tabFieldGroup}
              specification={vendorLeadProductSpecification}
              onChange={handleValuesChange}
            />
          ),
        }))) ??
      [],
    [vendorLeadProductSpecification, handleValuesChange]
  );

  const handleTabChange = useCallback(
    (newActiveKey: string) => {
      setVendorStepGuideKey(
        newActiveKey === ProductSpecificationFieldGroup.Quantities
          ? ProductSpecificationFieldGroup.Quantities
          : newActiveKey === ProductSpecificationFieldGroup.ProductDetails
            ? ProductSpecificationFieldGroup.ProductDetails
            : newActiveKey === ProductSpecificationFieldGroup.ShippingDetails
              ? ProductSpecificationFieldGroup.ShippingDetails
              : null
      );

      setCurrentTab(newActiveKey as ProductSpecificationFieldGroup);
    },
    [setVendorStepGuideKey]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: false
  useEffect(() => {
    setVendorStepGuideKey(
      currentTab === ProductSpecificationFieldGroup.Quantities
        ? ProductSpecificationFieldGroup.Quantities
        : currentTab === ProductSpecificationFieldGroup.ProductDetails
          ? ProductSpecificationFieldGroup.ProductDetails
          : currentTab === ProductSpecificationFieldGroup.ShippingDetails
            ? ProductSpecificationFieldGroup.ShippingDetails
            : null
    );
  }, [currentTab]);

  if (!vendorLeadProduct || !vendorLeadProductSpecification) {
    return null;
  }

  return (
    <Formik<IVendorLeadDescriptionStepFormValues>
      // Load whole specData object (do not filter by group). There are some problems with form re-initialization.
      // In Description tab (2nd tab) there are empty values
      initialValues={toFormVendorProductLeadSpecificationEditInitialValues(
        vendorLeadProductSpecification.fields,
        vendorLeadProduct
      )}
      validationSchema={formProductLeadSpecificationEditValidationSchema(vendorLeadProductSpecification.fields)}
      validateOnMount={true}
      enableReinitialize={true}
      onSubmit={handleSubmit}
    >
      <VendorLeadStepLayout>
        <div className="vendor-lead-step-description__container">
          <Tabs
            items={tabs}
            tabBarStyle={{ backgroundColor: '#F2F3F5', margin: '0 -100%', padding: '0 100%' }}
            animated={false}
            destroyInactiveTabPane={false}
            activeKey={currentTab}
            onChange={handleTabChange}
          />
        </div>
      </VendorLeadStepLayout>
    </Formik>
  );
}
